import type { BaseAssetSettings } from '../../BaseAsset';
import { getAttributesToSerialize } from '../../../attributes';
import { TextAssetStyle } from '../../TextAsset/TextAssetStyle';
import { Transcript } from '../../../Transcript';
import type { FancyCaptionRendererSettings } from '../renderer/FancyCaptionRenderer';
import { FancyCaptionRenderer } from '../renderer/FancyCaptionRenderer';
import type { TextStyleRendererSettings } from '../../TextAsset/renderer/TextStyleRenderer';
import { TextStyleRenderer } from '../../TextAsset/renderer/TextStyleRenderer';
import { BaseTextAsset } from '../../BaseTextAsset';
import { tallestStringsFromTranscript } from '../../TextAsset/metrics/TextMetrics';
import { CanvasFontSizeToFitAllTextBinarySearch } from '../../TextAsset/metrics/FontSizeToFitAllTextBinarySearch';
import { MAX_WIDTH_ACROSS_ALL_ASPECT_RATIOS } from '../../../../aspectRatioConfig';
import type { RawAssetData } from '../../../../video-data/types';
import { Config } from '../../../../video-data/config';
import type { PlaybackRegion } from '../../../PlaybackRegions';
import { PlaybackRegions } from '../../../PlaybackRegions';
import type { TextDirection } from '../../../../enums';
import { buildMetricsForFancyCaptions } from '../metrics/FancyCaptionMetrics';
import { buildCuesFromCaptions, buildSRTFromCaptions } from '../../captionUtils';
import { reaction } from '../../../reactivity';

export class FancyCaptionAsset extends BaseTextAsset<FancyCaptionRenderer> {
    override _classType = 'CanvasFancyCaptionAsset';

    normalStyle: TextAssetStyle;
    spokenWordStyle: TextAssetStyle;
    #isSpokenWordStyleEnabled: boolean = false;

    get isSpokenWordStyleEnabled() {
        return this.#isSpokenWordStyleEnabled;
    }

    set isSpokenWordStyleEnabled(val: boolean) {
        this.#isSpokenWordStyleEnabled = val;
        if (this.renderer) {
            this.renderer.isSpokenWordStyleEnabled = val;
        }
        this.requiresRedraw = true;
    }

    transcript: Transcript = new Transcript({
        parent: this,
        fetchResources: this.fetchResources,
        autoUpdate: this.autoDraw,
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: BaseAssetSettings) {
        const renderer = new FancyCaptionRenderer();
        super(container, settings, renderer);

        const styleSettings = {
            parent: this,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
        };
        this.normalStyle = new TextAssetStyle(styleSettings);
        this.spokenWordStyle = new TextAssetStyle(styleSettings);

        this.addReaction(
            reaction(
                () => this.visible,
                (visible) => {
                    if (visible && this.#builtWhileHidden) {
                        this.#builtWhileHidden = false;
                        this.requiresRedraw = true;
                    }
                },
                { name: 'FancyCaptionAsset.rebuildWhenShown' },
            ),
        );
    }

    #builtWhileHidden = false;

    protected override _build(): void {
        super._build();
        this.#builtWhileHidden = !this.visible;
    }

    override get styles() {
        return { normalStyle: this.normalStyle, spokenWordStyle: this.spokenWordStyle };
    }

    get hasText() {
        return this.transcript._words.length !== 0;
    }

    /**
     * The direction in which the text flows. This property affects text metrics.
     */
    get textDirection(): TextDirection {
        return this.normalStyle.textDirection;
    }

    set textDirection(value: TextDirection) {
        if (this.normalStyle.textDirection === value) {
            return;
        }
        this.normalStyle.textDirection = value;
        this.spokenWordStyle.textDirection = value;
        this.requiresRedraw = true;
    }

    override get stylePropertiesToCopy(): string[] {
        return [
            'textSize',
            'textAlign',
            'leadingMultiplier',
            'maxHeight',
            'normalStyle',
            'spokenWordStyle',
            'isSpokenWordStyleEnabled',
        ];
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'normalStyle',
            'spokenWordStyle',
            'isSpokenWordStyleEnabled',
        ]);
    }

    /** get a list of progress values where each chunk is fully visible */
    getPreviewRelTimes(): number[] {
        if (!this.renderer.canRender) {
            return [];
        }
        return Array.from({ length: this.chunkCount }, (_, i) => this.getPreviewRelTimeForChunk(i));
    }

    /** returns the progress value for the given chunk to render a preview ensuring it is fully visible **/
    getPreviewRelTimeForChunk(chunkIndex: number) {
        if (!this.renderer.canRender) {
            return 0;
        }
        let { endTime } = this.renderer.getTranscriptChunkTimeRange(chunkIndex);
        if (endTime > 0) {
            endTime -= 1e-3;
        }
        const playbackEndTime = PlaybackRegions.assetTimeToPlaybackTime(
            this.playbackRegions,
            Infinity, // the chunk endTime cannot be past the last playback region
            endTime,
        );
        const playbackStartTime = PlaybackRegions.assetTimeToPlaybackTime(
            this.playbackRegions,
            Infinity, // the start time of the playback regions cannot be past the last playback region
            PlaybackRegions.getStartTime(this.playbackRegions),
        );
        const progress =
            (playbackEndTime - playbackStartTime) /
            PlaybackRegions.getDurationFromPlaybackRegions(this.playbackRegions);
        return progress;
    }

    /** Returns the number of chunks that the renderer has. */
    get chunkCount() {
        return this.renderer.chunkCount;
    }

    async generateSRTText(
        timeOffset: number,
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
        captionAssetDuration: number,
    ): Promise<string[]> {
        return buildSRTFromCaptions(
            this,
            buildMetricsForFancyCaptions,
            this.renderer.canRender,
            timeOffset,
            playbackRegions,
            captionAssetDuration,
        );
    }

    async generateVTTText(
        timeOffset: number,
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
        captionAssetDuration: number,
    ): Promise<string[]> {
        return buildCuesFromCaptions(
            this,
            buildMetricsForFancyCaptions,
            this.renderer.canRender,
            timeOffset,
            playbackRegions,
            captionAssetDuration,
            'vtt',
        );
    }

    /** Sync the font size and leading to the style object. */
    _syncFontSizeAndLeadingToStyles() {
        for (const style of Object.values(this.styles)) {
            style.fontSize = this.textSize;
            style.leading = this.textSize * this.leadingMultiplier;
        }
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    override getRendererSettings(): FancyCaptionRendererSettings {
        this._syncFontSizeAndLeadingToStyles();
        const normalStyleSettings =
            this.normalStyle.getRendererSettings() as TextStyleRendererSettings;
        const spokenWordStyleSettings: TextStyleRendererSettings = {
            ...this.normalStyle.getRendererSettings(),
            fill: this.spokenWordStyle.getRendererSettings().fill,
            graphics: this.spokenWordStyle.getRendererSettings().graphics,
        };
        return {
            ...super.getRendererSettings(),
            transcript: this.transcript,
            alignment: this.textAlign,
            normalStyle: normalStyleSettings,
            spokenWordStyle: spokenWordStyleSettings,
            isSpokenWordStyleEnabled: this.isSpokenWordStyleEnabled,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            ...getAttributesToSerialize(this),
            'normalStyle',
            'spokenWordStyle',
        ]);
    }

    override set autoDraw(value: boolean) {
        if (this.autoDraw === value) {
            return;
        }
        super.autoDraw = value;
        // Because the transcript extends from TimeInvariantBase and isn't a BaseAsset,
        // we need to manually connect the two different update loops
        this.transcript.autoUpdate = value;
    }

    override get autoDraw() {
        return super.autoDraw;
    }

    override _setAutoDrawForAllChildren(value: boolean) {
        super._setAutoDrawForAllChildren(value);
        // connect the transcript to the autoDraw update loop
        this.transcript.autoUpdate = value;
    }

    /** Returns the maximum font size for the given font that can fit on one line. */
    getFontSizeToFitTallestText(fontSizeRange = { min: 50, max: 250 }) {
        const settings = this.getRendererSettings();
        const style = new TextStyleRenderer();
        style.apply(settings.normalStyle);

        const canvasFontSizeRange = {
            min: Math.floor(fontSizeRange.min * this.referenceToOutputMultiplier),
            max: Math.floor(fontSizeRange.max * this.referenceToOutputMultiplier),
        };

        // get the tallest text that can fit on one line
        const tallestText = this.renderer.hasChunks
            ? this.renderer.getTallestText()
            : tallestStringsFromTranscript(this.transcript, style).join('');

        // it's possible the transcript is empty (has no text)
        if (tallestText === '') {
            return {
                fontSize: this.normalStyle.fontSize,
                displaysAllText: true,
                tallestText: '',
            };
        }

        const { fontSize: canvasFontSize, displaysAllText } =
            CanvasFontSizeToFitAllTextBinarySearch.search({
                text: tallestText,
                alignment: settings.alignment,
                normalStyle: style,
                highlightStyle: style,
                highlightAreas: [],
                // since we're only after the tallest text that can fit on one line we don't need to worry about respecting
                // the current maxWidth which is why we're using a big number here. Also note that this is a canvas value,
                // not a luminary base value
                wordBreak: true,
                maxWidth: MAX_WIDTH_ACROSS_ALL_ASPECT_RATIOS,
                maxHeight: settings.maxTextHeight,
                fontSizeRange: canvasFontSizeRange,
            });

        const assetFontSize = Math.floor(canvasFontSize / this.referenceToOutputMultiplier);

        return {
            fontSize: assetFontSize,
            displaysAllText,
            tallestText,
        };
    }

    override _loadStylePropertiesForSwap(data: RawAssetData) {
        super._loadStylePropertiesForSwap(data);

        if (data.classType === Config.TEXT_ASSET_TYPE) {
            const normalStyle = data.normalStyle as { fontUrl: string; fill: number };
            const spokenWordStyle = data.spokenWordStyle as
                | { fontUrl?: string; fill?: number }
                | undefined;
            this.normalStyle.fontUrl = normalStyle.fontUrl;
            this.normalStyle.fill = normalStyle.fill;
            this.spokenWordStyle.fontUrl = spokenWordStyle?.fontUrl || normalStyle.fontUrl;
            this.spokenWordStyle.fill = spokenWordStyle?.fill || normalStyle.fill;
        }
    }
}

/**
 * The playback regions a fancy caption asset runs over, taken from whichever of the scene or the
 * scene group drives its voice. Exposed so a derivation can depend on the regions themselves rather
 * than only on which assets exist.
 */
export const voicedrivenPlaybackRegionsFor = (
    asset: FancyCaptionAsset,
): Readonly<Readonly<PlaybackRegion>[]> => {
    if (asset.slide) {
        return asset.slide._calculateVoicedrivenPlaybackRegionsDuringScene();
    }

    const parent = asset.luminaryParent;
    if (Config.isSceneGroup(parent)) {
        return parent.voicedrivenAssetPlaybackRegions;
    }
    return [];
};

/**
 * Syncs the given transcript to the given fancy caption asset. We do this by transferring the minimal amount of data
 * or words thereby reducing the number of chunks which has a positive impact on performance.
 */
export const syncTranscriptData = (asset: FancyCaptionAsset, transcript: Transcript) => {
    const playbackRegions = voicedrivenPlaybackRegionsFor(asset);
    asset.playbackRegions = playbackRegions;
    asset.transcript.transcriptData = transcript.getDataForRange(playbackRegions);
};
